import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle, FileText, Calendar, UserCircle, XCircle, HelpCircle } from 'lucide-react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import './PageStatusDashboardPage.css';
import { useStatusDashboardTimeseries, useJobFilterOptions } from '../hooks/useApi';
import MultiSelectFilter from '../components/MultiSelectFilter';

const formatDateForInput = (iso: string | undefined): string => (iso ? iso.slice(0, 10) : '');

// Recharts' Legend defaults to alphabetically sorting by label text; pin it to the
// same order the <Line> series are declared in (対象URL数 → エラー → 広告記載なし).
const LEGEND_ORDER = ['total_url_count', 'error_count', 'no_ad_notation_count'];

const PageStatusDashboardPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [createdAfter, setCreatedAfter] = React.useState(searchParams.get('after') || '');
    const [createdBefore, setCreatedBefore] = React.useState(searchParams.get('before') || '');
    const [selectedGuidelines, setSelectedGuidelines] = React.useState<string[]>(
        (searchParams.get('guidelines') || '').split(',').filter(Boolean)
    );
    const [selectedCreators, setSelectedCreators] = React.useState<string[]>(
        (searchParams.get('creators') || '').split(',').filter(Boolean)
    );

    // Keep URL in sync so filters survive a refresh.
    useEffect(() => {
        const params: Record<string, string> = {};
        if (createdAfter) params.after = createdAfter;
        if (createdBefore) params.before = createdBefore;
        if (selectedGuidelines.length) params.guidelines = selectedGuidelines.join(',');
        if (selectedCreators.length) params.creators = selectedCreators.join(',');
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdAfter, createdBefore, selectedGuidelines, selectedCreators]);

    const timeseriesParams = useMemo(
        () => ({
            guidelineId: selectedGuidelines.length ? selectedGuidelines.join(',') : undefined,
            createdById: selectedCreators.length ? selectedCreators.join(',') : undefined,
            createdAfter: createdAfter || undefined,
            createdBefore: createdBefore || undefined,
        }),
        [selectedGuidelines, selectedCreators, createdAfter, createdBefore]
    );

    const { data: timeseriesData, loading: timeseriesLoading, error: timeseriesError } =
        useStatusDashboardTimeseries(timeseriesParams);
    const { data: jobFilterOptionsData } = useJobFilterOptions();

    const chartPoints = useMemo(() => timeseriesData?.points || [], [timeseriesData]);

    const guidelineOptions = useMemo(() => {
        const guidelines = (jobFilterOptionsData as any)?.data?.guidelines || [];
        return guidelines.map((g: any) => ({ label: g.name || g.id, value: g.id }));
    }, [jobFilterOptionsData]);

    const creatorOptions = useMemo(() => {
        const creators = (jobFilterOptionsData as any)?.data?.creators || [];
        return creators.map((c: any) => ({
            label: c.display_name || c.name || c.username || c.email || c.id,
            value: c.id,
        }));
    }, [jobFilterOptionsData]);

    const totals = useMemo(
        () =>
            chartPoints.reduce(
                (acc, p) => ({
                    totalUrlCount: acc.totalUrlCount + p.total_url_count,
                    errorCount: acc.errorCount + p.error_count,
                    noAdNotationCount: acc.noAdNotationCount + p.no_ad_notation_count,
                }),
                { totalUrlCount: 0, errorCount: 0, noAdNotationCount: 0 }
            ),
        [chartPoints]
    );

    return (
        <div className="page-container">
            <div className="page-header">
                <h1 className="page-title">ページステータスダッシュボード</h1>
                <p className="page-description">
                    エラーになったページ数と「広告記載なし」と判定されたページ数を月別に集計します。期間・ガイドライン・アップロード者で絞り込めます。
                </p>
            </div>

            {!timeseriesLoading && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon status-dashboard-stat-icon-neutral">
                            <FileText size={24} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{totals.totalUrlCount.toLocaleString()}</div>
                            <div className="stat-label">対象URL数</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon danger">
                            <XCircle size={24} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{totals.errorCount.toLocaleString()}</div>
                            <div className="stat-label">エラー件数</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon success">
                            <HelpCircle size={24} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{totals.noAdNotationCount.toLocaleString()}</div>
                            <div className="stat-label">広告記載なし件数</div>
                        </div>
                    </div>
                </div>
            )}

            <div className="status-dashboard-chart-section">
                {timeseriesError && (
                    <div className="error-state">
                        <AlertTriangle size={20} />
                        <span>{timeseriesError}</span>
                    </div>
                )}

                {timeseriesLoading ? (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>読み込み中...</span>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={360}>
                        <LineChart data={chartPoints} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                            <YAxis
                                tick={{ fontSize: 11 }}
                                label={{ value: 'URL数', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                            />
                            <Tooltip />
                            <Legend itemSorter={(item) => LEGEND_ORDER.indexOf(String(item.dataKey))} />
                            <Line type="monotone" dataKey="total_url_count" name="対象URL数" stroke="#000000" dot={false} />
                            <Line type="monotone" dataKey="error_count" name="エラー" stroke="#ef4444" dot={false} />
                            <Line type="monotone" dataKey="no_ad_notation_count" name="広告記載なし" stroke="#10b981" dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}

                {!timeseriesLoading && chartPoints.length === 0 && (
                    <div className="status-dashboard-empty">該当するデータがありません</div>
                )}
            </div>

            <div className="filters-section">
                <div className="filters-row">
                    <div className="filter-controls">
                        <div className="multi-select-filter status-dashboard-date-filter">
                            <label className="filter-label">
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Calendar size={12} />
                                    期間（ジョブ作成日時）
                                </span>
                            </label>
                            <div className="status-dashboard-date-range">
                                <input
                                    type="date"
                                    value={formatDateForInput(createdAfter)}
                                    onChange={(e) => setCreatedAfter(e.target.value)}
                                    className="status-dashboard-date-input"
                                />
                                <span className="status-dashboard-date-separator">〜</span>
                                <input
                                    type="date"
                                    value={formatDateForInput(createdBefore)}
                                    onChange={(e) => setCreatedBefore(e.target.value)}
                                    className="status-dashboard-date-input"
                                />
                            </div>
                        </div>

                        <MultiSelectFilter
                            label={<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FileText size={12} />ガイドライン</span>}
                            options={guidelineOptions}
                            selectedValues={selectedGuidelines}
                            onSelectionChange={setSelectedGuidelines}
                            placeholder="すべてのガイドライン"
                        />

                        <MultiSelectFilter
                            label={<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UserCircle size={12} />アップロード者</span>}
                            options={creatorOptions}
                            selectedValues={selectedCreators}
                            onSelectionChange={setSelectedCreators}
                            placeholder="すべてのアップロード者"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PageStatusDashboardPage;
